import { useState, useEffect } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";

const SETTINGS_META = {
  ai_temperature_chat: {
    label: "Chat Temperature",
    description: "Controls creativity in conversations. Lower = more focused, higher = more creative.",
    default: 0.7,
  },
  ai_temperature_routine: {
    label: "Routine Temperature",
    description: "Controls creativity when executing scheduled routines.",
    default: 0.7,
  },
  ai_temperature_creative: {
    label: "Creative Temperature",
    description: "Controls creativity for emails and notifications.",
    default: 0.9,
  },
  ai_temperature_muninn: {
    label: "Muninn Temperature",
    description: "Controls creativity for Muninn research agent. Lower = more factual, higher = more exploratory.",
    default: 0.2,
  },
};

function AnnaSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setLoading(true);
    setError("");
    try {
      const data = await apiClient.get("/ai-settings");
      setSettings(data.settings || {});
    } catch (err) {
      setError(err.message || "Failed to load AI settings");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(key, value) {
    setSettings((prev) => ({ ...prev, [key]: parseFloat(value) }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await apiClient.put("/ai-settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="anna-settings"><p className="loading-text">Loading AI settings...</p></div>;
  }

  return (
    <div className="anna-settings">
      <div className="anna-settings-header">
        <div>
          <h3>Anna&apos;s AI Settings</h3>
          <p className="anna-settings-description">
            Configure how Anna&apos;s AI behaves. Temperature controls randomness: 0.0 = deterministic, 1.0 = maximum creativity.
          </p>
        </div>
        <RefreshButton onClick={loadSettings} title="Refresh AI settings" />
      </div>

      {error && <div className="anna-settings-error">{error}</div>}

      <div className="anna-settings-list">
        {Object.entries(SETTINGS_META).map(([key, meta]) => {
          const value = settings[key] ?? meta.default;
          return (
            <div key={key} className="anna-setting-card">
              <div className="setting-card-header">
                <span className="setting-label">{meta.label}</span>
                <span className="setting-value-badge">{value.toFixed(2)}</span>
              </div>
              <p className="setting-description">{meta.description}</p>
              <div className="setting-slider-row">
                <span className="slider-bound">0.0</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={value}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="setting-slider"
                />
                <span className="slider-bound">1.0</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="anna-settings-actions">
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </button>
        {saved && <span className="save-success">✓ Saved</span>}
      </div>
    </div>
  );
}

export default AnnaSettings;
